import React, { useState } from "react";
import styled from "styled-components";
import { EmailRounded, PhoneRounded, LocationOnRounded } from "@mui/icons-material";

const Container = styled.div`
  flex: 1;
  overflow-y: auto;
  background: ${({ theme }) => theme.bg};
  padding: 60px 40px 100px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
  box-sizing: border-box;
  @media (max-width: 768px) {
    padding: 40px 16px 80px 16px;
  }
`;

const PageTitle = styled.h1`
  font-size: 42px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 3px;
  color: ${({ theme }) => theme.text_primary};
  margin: 0;
  text-align: center;
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.text_secondary};
  text-align: center;
  margin: -32px 0 0 0;
  max-width: 500px;
`;

const Content = styled.div`
  width: 100%;
  max-width: 1000px;
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 40px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const InfoTitle = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
`;

const InfoDesc = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.7;
  margin: -16px 0 0 0;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const IconBox = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: ${({ theme }) => theme.text_primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const InfoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${({ theme }) => theme.text_secondary};
`;

const InfoValue = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.text_primary};
`;

const FormSide = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${({ theme }) => theme.text_secondary};
`;

const Input = styled.input`
  padding: 12px 14px;
  border: 1.5px solid ${({ theme }) => theme.text_secondary + 40};
  border-radius: 8px;
  font-size: 15px;
  background: transparent;
  color: ${({ theme }) => theme.text_primary};
  outline: none;
  transition: border 0.2s;
  &:focus {
    border-color: ${({ theme }) => theme.text_primary};
  }
  &::placeholder {
    color: ${({ theme }) => theme.text_secondary + 80};
  }
`;

const Textarea = styled.textarea`
  padding: 12px 14px;
  border: 1.5px solid ${({ theme }) => theme.text_secondary + 40};
  border-radius: 8px;
  font-size: 15px;
  background: transparent;
  color: ${({ theme }) => theme.text_primary};
  outline: none;
  resize: none;
  height: 140px;
  font-family: inherit;
  transition: border 0.2s;
  &:focus {
    border-color: ${({ theme }) => theme.text_primary};
  }
  &::placeholder {
    color: ${({ theme }) => theme.text_secondary + 80};
  }
`;

const SubmitButton = styled.button`
  padding: 14px;
  background: ${({ theme }) => theme.text_primary};
  color: ${({ theme }) => theme.bg};
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.85;
  }
`;

const SuccessMsg = styled.div`
  padding: 14px;
  background: #e6f4ea;
  color: #2e7d32;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
`;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setForm({ name: "", email: "", subject: "", message: "" });
  };

  return (
    <Container>
      <PageTitle>Contact Us</PageTitle>
      <Subtitle>
        Have a question or need help? We'd love to hear from you. Send us a message and we'll get back to you shortly.
      </Subtitle>

      <Content>
        <InfoSide>
          <div>
            <InfoTitle>Get in Touch</InfoTitle>
            <InfoDesc>
              Our team is available Monday to Saturday, 9am to 6pm. We typically respond within 24 hours.
            </InfoDesc>
          </div>

          <InfoItem>
            <IconBox>
              <LocationOnRounded sx={{ color: "#fff", fontSize: "20px" }} />
            </IconBox>
            <InfoText>
              <InfoLabel>Address</InfoLabel>
              <InfoValue>123 Fashion Street, Gulberg III</InfoValue>
              <InfoValue>Lahore, Pakistan</InfoValue>
            </InfoText>
          </InfoItem>

          <InfoItem>
            <IconBox>
              <EmailRounded sx={{ color: "#fff", fontSize: "20px" }} />
            </IconBox>
            <InfoText>
              <InfoLabel>Email</InfoLabel>
              <InfoValue>support@krist.com</InfoValue>
            </InfoText>
          </InfoItem>

          <InfoItem>
            <IconBox>
              <PhoneRounded sx={{ color: "#fff", fontSize: "20px" }} />
            </IconBox>
            <InfoText>
              <InfoLabel>Phone</InfoLabel>
              <InfoValue>+92 300 1234567</InfoValue>
            </InfoText>
          </InfoItem>
        </InfoSide>

        <FormSide onSubmit={handleSubmit}>
          <Row>
            <InputWrapper>
              <Label>Name</Label>
              <Input
                name="name"
                placeholder="Your name"
                value={form.name}
                onChange={handleChange}
                required
              />
            </InputWrapper>
            <InputWrapper>
              <Label>Email</Label>
              <Input
                name="email"
                type="email"
                placeholder="Your email"
                value={form.email}
                onChange={handleChange}
                required
              />
            </InputWrapper>
          </Row>
          <InputWrapper>
            <Label>Subject</Label>
            <Input
              name="subject"
              placeholder="What's this about?"
              value={form.subject}
              onChange={handleChange}
              required
            />
          </InputWrapper>
          <InputWrapper>
            <Label>Message</Label>
            <Textarea
              name="message"
              placeholder="Write your message here..."
              value={form.message}
              onChange={handleChange}
              required
            />
          </InputWrapper>
          {submitted && <SuccessMsg>✓ Message sent! We'll get back to you soon.</SuccessMsg>}
          <SubmitButton type="submit">Send Message</SubmitButton>
        </FormSide>
      </Content>
    </Container>
  );
};

export default Contact;